
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { NotificationFormData, NotificationProductData } from "@/types/notifications";
import { sendDecoprofWebhook, sendRecipientsWebhook } from "./webhookService";
import { addAutoMonitoringForNotification } from "./autoMonitoringService";

export const createNotificationInDb = async (
  notification: NotificationFormData
): Promise<any> => {
  console.log("Starting notification creation with form data:", notification);
  
  // Validation checks
  if (!notification.message.trim()) {
    throw new Error("Bericht is verplicht");
  }
  
  // Collect recipients
  const internalRecipients: string[] = [];
  if (notification.recipients.internal.group1) {
    internalRecipients.push("group1");
  }
  if (notification.recipients.internal.group2) {
    internalRecipients.push("group2");
  }
  
  console.log("Internal recipients:", internalRecipients);
  console.log("External recipients:", notification.recipients.external);
  
  // Ensure user ID is available
  if (!notification.userId) {
    throw new Error("User ID is required");
  }
  
  // Always ensure priority has a valid string value, never null
  const priorityValue = notification.priority === null ? "normaal" : notification.priority;
  
  try {
    // Insert the notification
    console.log("Inserting notification with data:", {
      type: notification.type,
      vo_number: notification.voNumber || null,
      exact_order_number: notification.exactOrderNumber || null,
      message: notification.message,
      priority: priorityValue,
      status: notification.status,
      user_id: notification.userId,
      user_name: notification.userName || "Onbekende gebruiker"
    });
    
    const { data: notificationData, error: notificationError } = await supabase
      .from('notifications')
      .insert({
        type: notification.type,
        vo_number: notification.voNumber || null,
        exact_order_number: notification.exactOrderNumber || null,
        message: notification.message,
        priority: priorityValue, // Use default "normaal" if null
        status: notification.status,
        user_id: notification.userId,
        user_name: notification.userName || "Onbekende gebruiker"
      })
      .select()
      .single();
    
    if (notificationError) {
      console.error("Error inserting notification:", notificationError);
      throw notificationError;
    }
    
    console.log("Notification created successfully:", notificationData);
    
    // Auto-add products to monitoring for awaiting stock statuses
    try {
      await addAutoMonitoringForNotification(
        notificationData.id,
        notification.message,
        notification.status
      );
    } catch (autoMonitorError) {
      console.error("Error in auto-monitoring:", autoMonitorError);
      // Don't fail the notification creation
    }
    
    // Insert product data if Inkoop Goes is selected
    if (notification.recipients.internal.group1 && notification.products && notification.products.length > 0) {
      const productData = notification.products.map(product => ({
        notification_id: notificationData.id,
        product_name: product.product_name,
        product_sku: product.product_sku, 
        product_bc: product.product_bc,
        product_quantity: product.product_quantity,
        product_color: product.product_color || null,
        is_monitored: true
      }));
      
      console.log("Inserting product data:", productData);
      
      const { error: productError } = await supabase
        .from('notification_product_monitoring')
        .insert(productData);
      
      if (productError) {
        console.error("Error inserting product data:", productError);
        toast.error("Waarschuwing: Niet alle productgegevens zijn opgeslagen");
      } else {
        console.log("Product data saved successfully");
      }
    }
    
    // Insert recipients if any
    const allRecipients = [...internalRecipients, ...notification.recipients.external];
    let recipientsSaved = false;
    
    if (allRecipients.length > 0) {
      const recipientsData = allRecipients.map(recipient => ({
        notification_id: notificationData.id,
        recipient_type: internalRecipients.includes(recipient) ? 'internal' : 'external',
        recipient_name: recipient
      }));
      
      console.log("Inserting recipients:", recipientsData);
      
      const { error: recipientsError } = await supabase
        .from('notification_recipients')
        .insert(recipientsData);
      
      if (recipientsError) {
        console.error("Error inserting recipients:", recipientsError);
        toast.error("Waarschuwing: Niet alle ontvangers zijn opgeslagen");
      } else {
        console.log("Recipients saved successfully");
        recipientsSaved = true;
      }
      
      // Send webhook ONLY for notifications where "Inkoop Goes" (group1) is selected
      if (recipientsSaved && notification.recipients.internal.group1) {
        console.log("Triggering recipients webhook because Inkoop Goes is selected:", {
          notification_id: notificationData.id,
          internal: internalRecipients,
          total_recipients: allRecipients.length
        });
        
        try {
          // Add products to the notification data for the webhook
          const notificationWithProducts = {
            ...notificationData,
            products: notification.products || []
          };
          
          const webhookResult = await sendRecipientsWebhook(
            notificationWithProducts,
            internalRecipients,
            notification.recipients.external
          );
          
          console.log("Email recipients webhook result:", webhookResult ? "Success" : "Failed");
          
          if (!webhookResult) {
            toast.error("De melding is aangemaakt, maar de webhook voor ontvangers kon niet worden verzonden.");
          }
        } catch (webhookError) {
          console.error("Error sending recipients webhook:", webhookError);
          toast.error("De melding is aangemaakt, maar de webhook voor ontvangers kon niet worden verzonden.");
        }
      } else {
        console.log("Skipping recipients webhook - Inkoop Goes not selected");
      }
    } else {
      console.log("No recipients to notify, skipping recipient saving");
    }
    
    // Send status webhook if status is "actie_decoprof"
    if (notification.status === "actie_decoprof") {
      console.log("Triggering status webhook for actie_decoprof notification");
      try {
        const webhookResult = await sendDecoprofWebhook(notificationData, null);
        console.log("Status webhook result:", webhookResult ? "Success" : "Failed");
        
        if (!webhookResult) {
          toast.error("De melding is aangemaakt, maar de status webhook kon niet worden verzonden.");
        }
      } catch (webhookError) {
        console.error("Error sending status webhook:", webhookError);
        toast.error("De melding is aangemaakt, maar de status webhook kon niet worden verzonden.");
      }
    }
    
    return notificationData;
  } catch (error) {
    console.error("Error in createNotificationInDb:", error);
    throw error;
  }
};
